import type { OrgUser, UpdateUserInput } from '@pm/schemas';
import { displayPhone, formatPhone } from '@pm/schemas';
import { Button, Dialog, DialogContent, DialogFooter, Field, Input } from '@pm/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AvatarUploader } from '@/components/AvatarUploader';
import { api, ApiRequestError } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { useToast } from '@/lib/toast';

/** Edição completa do usuário pelo administrador: foto, nome, e-mail, telefone e senha. */
export function EditUserDialog({
  user,
  onOpenChange,
}: {
  user: OrgUser | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent title={`Editar ${user?.name ?? ''}`} size="md">
        {user ? (
          <EditUserForm key={user.id} user={user} onClose={() => onOpenChange(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function EditUserForm({ user, onClose }: { user: OrgUser; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const me = useMe();
  const { refetchMe } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url);
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(displayPhone(user.phone));
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string }>({});
  const [saving, setSaving] = useState(false);
  const locked = user.shared_account;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['users'] });
    if (user.id === me.user.id) await refetchMe();
  };

  async function save() {
    const errs: typeof errors = {};
    if (name.trim().length < 2) errs.name = 'Informe o nome.';
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) errs.email = 'Informe um e-mail válido.';
    if (password && password.length < 8) errs.password = 'Ao menos 8 caracteres.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const body: UpdateUserInput = {};
    if (name.trim() !== user.name) body.name = name.trim();
    if (email.trim().toLowerCase() !== user.email.toLowerCase()) body.email = email.trim();
    if (phone.trim() !== displayPhone(user.phone)) body.phone = phone.trim() || null;
    if (password) body.password = password;
    if (Object.keys(body).length === 0) return onClose();

    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, body);
      await refresh();
      toast.success(
        'Usuário atualizado',
        body.password
          ? 'A nova senha vale a partir de agora; as sessões dele foram encerradas.'
          : undefined,
      );
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'USER_EMAIL_TAKEN')
        setErrors({ email: err.message });
      else toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <AvatarUploader
        name={user.name}
        url={avatarUrl}
        onUpload={async (body) => {
          const next = await api.put<OrgUser>(`/users/${user.id}/avatar`, body);
          setAvatarUrl(next.avatar_url);
          await refresh();
        }}
        onRemove={async () => {
          await api.delete(`/users/${user.id}/avatar`);
          setAvatarUrl(null);
          await refresh();
        }}
      />
      <Field label="Nome" htmlFor="eu-name" error={errors.name}>
        <Input
          id="eu-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
        />
      </Field>
      {locked ? (
        <p className="rounded border border-border bg-surface-2 px-3 py-2 text-sm text-text-2">
          Esta pessoa também participa de outra organização. Por segurança, e-mail e senha só podem
          ser alterados por ela mesma, em Meu perfil.
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="E-mail" htmlFor="eu-email" error={errors.email}>
          <Input
            id="eu-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={locked}
            maxLength={254}
          />
        </Field>
        <Field label="Telefone" htmlFor="eu-phone">
          <Input
            id="eu-phone"
            mono
            type="tel"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            placeholder="(11) 98765-4321"
            maxLength={19}
          />
        </Field>
      </div>
      <Field
        label="Nova senha (opcional)"
        htmlFor="eu-password"
        error={errors.password}
        hint="Deixe em branco para manter. Ao definir, as sessões abertas dessa pessoa são encerradas."
      >
        <Input
          id="eu-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={locked}
          maxLength={128}
        />
      </Field>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}
